// Printed pages for the desk: the half-letter card for one place (a guest takes it
// with them) and the morning sheet (the day's local answers on one letter page). Both
// are built only from what the page already loaded; every value is escaped. The
// forecast is not printed: Weather Intelligence owns it on its own page.
const esc = (v) => String(v ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');

const FONT = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";

const shell = (title, pageSize, body) => `<!doctype html>
<html lang="en"><head><meta charset="utf-8"><title>${esc(title)}</title>
<style>
  @page { size: ${pageSize}; margin: 0.45in; }
  * { box-sizing: border-box; }
  body { font-family: ${FONT}; color: #111827; margin: 0; }
  h1 { font-size: 22px; margin: 0 0 4px; }
  h2 { font-size: 11px; text-transform: uppercase; letter-spacing: .06em; color: #6b7280; margin: 18px 0 6px; }
  p, li { font-size: 13px; line-height: 1.45; margin: 0 0 4px; }
  ul { margin: 0; padding-left: 16px; }
  .kick { font-size: 11px; text-transform: uppercase; letter-spacing: .08em; color: #6b7280; margin-bottom: 8px; }
  .muted { color: #6b7280; }
  .big { font-size: 26px; font-weight: 600; margin: 10px 0; }
  .tip { border-left: 3px solid #32B1FF; padding: 6px 10px; background: #f0f9ff; margin: 10px 0; }
  .qr { display: flex; align-items: center; gap: 14px; margin-top: 18px; }
  .qr img { width: 1.9in; height: 1.9in; }
  .cols { columns: 2; column-gap: 28px; }
  .cols section { break-inside: avoid; }
</style></head>
<body onload="setTimeout(function(){window.print()},300)">${body}</body></html>`;

export const buildPlaceCardHtml = ({ parkName, place, qrUrl, placeUrl }) => {
  const p = place || {};
  const lines = [p.line, p.hours_label && !String(p.line || '').includes(p.hours_label) ? p.hours_label : null].filter(Boolean);
  const body = `
    <div class="kick">From the front desk${parkName ? ` at ${esc(parkName)}` : ''}</div>
    <h1>${esc(p.name)}</h1>
    ${p.category_label ? `<p class="muted">${esc(p.category_label)}</p>` : ''}
    ${lines.map((l) => `<p>${esc(l)}</p>`).join('')}
    ${p.phone ? `<div class="big">${esc(p.phone)}</div>` : ''}
    ${p.address ? `<p>${esc(p.address)}</p>` : ''}
    ${p.staff?.owner_note ? `<div class="tip">${esc(p.staff.owner_note)}</div>` : ''}
    ${qrUrl ? `<div class="qr"><img src="${esc(qrUrl)}" alt=""><p>Scan for directions, hours and the menu.${placeUrl ? `<br><span class="muted">${esc(placeUrl)}</span>` : ''}</p></div>` : ''}`;
  return shell(p.name || 'Place', '5.5in 8.5in', body);
};

const panelOf = (page, key) => (page?.panels || []).find((x) => x.panel === key);

export const buildMorningSheetHtml = ({ parkName, page }) => {
  const triage = panelOf(page, 'triage')?.facts?.rows || [];
  const blocks = panelOf(page, 'right_now')?.facts?.blocks || [];
  const openNow = panelOf(page, 'open_now');
  const picks = (openNow?.facts?.picks || []).slice(0, 8);
  const tiles = panelOf(page, 'occasions')?.facts?.tiles || [];
  const requests = panelOf(page, 'requests')?.facts?.rows || [];
  const changes = panelOf(page, 'changes')?.facts || {};
  const closed = changes.closed?.rows || [];
  const fresh = changes.new_on_map?.rows || [];

  const help = triage.length ? `<section><h2>Help nearby</h2><ul>${triage.map((r) => {
    const no = r.nearest_open;
    const openLine = no && no.name && !String(r.fact_label || '').includes(no.name)
      ? ` <span class="muted">Open now: ${esc(no.name)}${no.hours_label ? `, ${esc(no.hours_label)}` : ''}${no.phone ? ` · ${esc(no.phone)}` : ''}</span>` : '';
    return `<li>${esc(r.fact_label || r.label)}${openLine}</li>`;
  }).join('')}</ul></section>` : '';

  const now = blocks.length ? `<section><h2>${esc(panelOf(page, 'right_now')?.title || 'Right now')}</h2>${blocks.map((b) => `
      <p><strong>${esc(b.label)}</strong></p><ul>${(b.answers || []).slice(0, 3).map((a) => `<li>${esc(a.line || a.name)}${a.phone ? ` · ${esc(a.phone)}` : ''}</li>`).join('')}</ul>`).join('')}</section>` : '';

  const tonight = picks.length ? `<section><h2>${esc(openNow?.title || 'Open tonight')}</h2><ul>${picks.map((a) => `<li>${esc(a.line || a.name)}${a.phone ? ` · ${esc(a.phone)}` : ''}</li>`).join('')}</ul></section>` : '';

  const occ = tiles.filter((t) => t.top_pick).length ? `<section><h2>${esc(panelOf(page, 'occasions')?.title || 'Find a place for')}</h2><ul>${tiles.filter((t) => t.top_pick).map((t) => `<li>${esc(t.label)}: ${esc(t.top_pick)}</li>`).join('')}</ul></section>` : '';

  const req = requests.length ? `<section><h2>Booking requests</h2><ul>${requests.map((r) => `<li>${esc(r.business)}${r.when_label ? `, ${esc(r.when_label)}` : ''}${r.party_size ? `, party of ${esc(r.party_size)}` : ''} · ${esc(r.state_label)}</li>`).join('')}</ul></section>` : '';

  const chg = closed.length || fresh.length ? `<section><h2>Closed or new</h2><ul>${closed.map((r) => `<li>${esc(r.name)}: ${esc(r.label)}</li>`).join('')}${fresh.map((r) => `<li>${esc(r.name)}: new${r.since_label ? ` since ${esc(r.since_label)}` : ''}</li>`).join('')}</ul></section>` : '';

  const body = `
    <div class="kick">${esc(parkName || '')}${parkName ? ' · ' : ''}Around the Park</div>
    <h1>Morning sheet</h1>
    <p class="muted">${esc(page?.as_of_label || '')}</p>
    ${page?.verdict ? `<p>${esc(page.verdict)}</p>` : ''}
    <div class="cols">${help}${now}${tonight}${occ}${req}${chg}</div>`;
  return shell(`${parkName ? `${parkName} — ` : ''}Morning sheet`, 'letter', body);
};

// Opens the page in a new window; the page prints itself once its images have loaded.
export const openPrint = (html) => {
  const w = window.open('', '_blank');
  if (!w) return false;
  w.document.open();
  w.document.write(html);
  w.document.close();
  w.focus();
  return true;
};
